import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, Button, CircularProgress, Alert } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import {
  getPublicDivision, listPublicDivisionTeams, listPublicDivisionGames, listPublicTeamGames,
} from '../../services/publicProgramService';
import { buildPublicPerTeam } from '../../utils/divisionSchedule';
import DivisionHub from '../../components/programs/DivisionHub';

// Phase C.4a — "THE DIVISION IS THE HUB." This is the PUBLIC half of the
// one Division Hub concept — it renders the exact same <DivisionHub>
// component the authenticated Division management surface
// (DivisionSchedulePage.js) does, fed only public-safe data. Unified
// presentation does NOT mean weakened security: every fetch below still
// goes through the same public-safe RPCs C.2/C.3/C.4 already
// established, and DivisionHub itself receives `canManage={false}` with
// no mutation handlers at all — there is nothing to hide with CSS,
// because there is nothing private in this component's own state.
//
// Coverage notes (an honest, pre-existing boundary — not a regression
// introduced here, and not one this pass can close without a new public
// RPC, which would require a migration change this phase explicitly
// defers):
//  - Internal Division matchups are ALWAYS included, regardless of
//    either participating Team's own public_page_enabled
//    (list_public_division_games, 042, already independent of it — same
//    precedent as list_public_division_teams, 038).
//  - An in-house Team's OWN external (Outside Program) games are only
//    visible here when THAT Team's own public_page_enabled is true
//    (list_public_team_games, 041) — an unpublished Team's external
//    games simply don't appear on the Division Hub, exactly as they
//    don't appear anywhere else public today. Its Division-matchup
//    contribution and record still show correctly regardless.
//
// Phase C.5e — the per-Team reshaping (`perTeamFromPublicData`) that
// used to live here privately is now buildPublicPerTeam() (divisionSchedule
// .js) — TeamSchedulePage.js's own public-safe records fallback needed
// the exact same reshaping and now shares this one implementation
// instead of a second copy. The extraction also fixed a latent gap:
// list_public_team_games() (041) never returns `event_type`, so an
// external Team's own Game was silently invisible to every event_type
// === 'game' check downstream (Upcoming/Results/Records) — internal
// matchups were never affected (mergeDivisionSchedule() already stamps
// event_type itself for those). buildPublicPerTeam() now stamps it.

export default function PublicDivisionPage() {
  const { programSlug, divisionId } = useParams();
  const navigate = useNavigate();
  const [division, setDivision] = useState(null);
  const [perTeam, setPerTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setError(null);

    getPublicDivision(divisionId).then(async (d) => {
      if (cancelled) return;
      if (!d) { setNotFound(true); setLoading(false); return; }
      setDivision(d);

      const teams = await listPublicDivisionTeams(divisionId);
      const [divisionGames, externalGamesEntries] = await Promise.all([
        listPublicDivisionGames(divisionId).catch(() => []),
        Promise.all(teams.map(async (team) => {
          if (!team.public_page_enabled) return [team.id, []];
          const games = await listPublicTeamGames(team.id).catch(() => []);
          return [team.id, games];
        })),
      ]);
      if (cancelled) return;

      const externalGamesByTeamId = Object.fromEntries(externalGamesEntries);
      setPerTeam(buildPublicPerTeam(teams, divisionGames, externalGamesByTeamId));
      setLoading(false);
    }).catch(e => {
      if (!cancelled) { setError(e.message); setLoading(false); }
    });

    return () => { cancelled = true; };
  }, [divisionId]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh' }}>
        <Navbar />
        <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress sx={{ color: '#2ea84b' }} /></Box>
      </Box>
    );
  }

  if (notFound) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Navbar />
        <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center', flex: 1 }}>
          <Typography sx={{ fontSize: '2.2rem', mb: 1.5 }}>🏈</Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Division not found</Typography>
          <Typography sx={{ color: '#9eb8d0', mb: 3 }}>
            This division doesn't exist or isn't public yet.
          </Typography>
          <Button variant="contained" color="primary" onClick={() => navigate(`/programs/${programSlug}`)} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Back to Program
          </Button>
        </Container>
        <Footer />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh' }}>
        <Navbar />
        <Container maxWidth="sm" sx={{ py: 8 }}><Alert severity="error">{error}</Alert></Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Navbar />
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 }, flex: 1 }}>
        <Button onClick={() => navigate(`/programs/${division.program_slug}`)} sx={{ textTransform: 'none', color: '#6a8aa8', mb: 2, pl: 0 }}>
          ← {division.program_name}
        </Button>

        <DivisionHub
          division={division}
          perTeam={perTeam}
          canManage={false}
          getTeamHref={(team) => {
            const full = perTeam.find(p => p.team.id === team.id)?.team;
            return full?.public_page_enabled ? `/programs/${division.program_slug}/teams/${team.id}` : null;
          }}
        />
      </Container>
      <Footer />
    </Box>
  );
}
